import type {
  CatalogLogoResponse,
  ImageRef,
  DeviceProfilesResponse,
  AdminDeviceListResponse,
  AdminUser,
  AdminUserListResponse,
  CreateUserRequest,
  UpdateUserRequest,
  SettingsListResponse,
  SettingsDocument,
  SourceCatalogResponse,
  JellyfinUsersResponse,
  UserSettingsListResponse,
  UserSettingsOverride,
  SettingsScope,
  ClientConfigResponse,
  AuditListResponse,
  AuthState,
  CatalogHomeResponse,
  CatalogItemDetails,
  CatalogListResponse,
  ClientCapabilities,
  DeviceSummary,
  DeviceWaiting,
  EnrollResponse,
  EnrollStatusResponse,
  HealthReadyResponse,
  LoginRequest,
  PlaybackSessionResponse,
  PlaybackSessionStatus,
  PlaybackErrorReport,
  PlaybackHeartbeat,
  CollectionResponse,
  LiveChannelListResponse,
  PersonCreditsResponse,
  SimilarResponse,
  EpgNowNextResponse,
  EpgScheduleResponse,
  SeasonEpisodesResponse,
  PluginListResponse,
  ProviderSourceKind,
  ProviderSourcesResponse,
  SourceVoicesResponse,
  SearchResponse,
  SessionResponse,
  FavoritesResponse,
  HistoryResponse,
  ReleaseMarkResponse,
  TitleChoice,
  TitleChoiceUpdate,
  PlaybackPreparationResponse,
  ContinueWatchingResponse,
  ProgressEntry,
  ProgressUpdate,
  SearchHistoryResponse,
  AdminMonitorResponse,
  MonitorTitleCard,
} from '@lumos/contracts';

import { API_PREFIX } from './wire.js';
import { setCsrfToken } from './client.js';
import { ApiError, apiRequest, readsViaPost, useReadsViaPost } from './client.js';

/**
 * Состояние входа.
 *
 * `?c=1` просит сервер ОТКАЗАТЬ запросу, пришедшему вовсе без cookie: есть
 * браузеры (телевизор владельца, журнал прода 06.10.2026), которые шлют
 * запросы чтения сначала без cookie и повторяют с ними только после отказа.
 * Вежливое 200 «не вошёл» такой браузер принимал как ответ — и одобренное
 * устройство оставалось перед формой входа. Тому, кому повторять нечем
 * (новый посетитель), состояние приходит внутри самого отказа.
 */
function readAuthState(signal: AbortSignal | undefined, direct: boolean): Promise<AuthState> {
  return apiRequest<AuthState>('/auth/state?c=1', { ...(signal ? { signal } : {}), ...(direct ? { direct } : {}) }).catch(
    (error: unknown) => {
      const state = error instanceof ApiError && error.status === 401 ? error.details?.state : undefined;
      if (state !== undefined && state !== null && typeof state === 'object') return state as AuthState;
      throw error;
    },
  );
}

export function getAuthState(signal?: AbortSignal): Promise<AuthState> {
  return readAuthState(signal, !readsViaPost())
    .then((state) => {
      // Сервер не увидел cookie устройства и после отказа. Спросим методом
      // POST: если там они есть, браузер не передаёт их в запросах чтения
      // вовсе — читаем в обход (`POST /lite/read`) и спрашиваем заново.
      if (state.authenticated || readsViaPost() || state.cookies === undefined || state.cookies.device) return state;
      return apiRequest<{ cookies: { device: boolean; session: boolean } }>('/auth/probe', { method: 'POST', body: {} })
        .then((seen) => {
          if (!seen.cookies.device && !seen.cookies.session) return state;
          useReadsViaPost();
          return readAuthState(signal, false);
        })
        .catch(() => state);
    })
    .then((state) => {
      setCsrfToken(state.csrfToken);
      return state;
    });
}

export function login(body: LoginRequest): Promise<SessionResponse> {
  return apiRequest<SessionResponse>('/auth/login', { method: 'POST', body }).then((session) => {
    setCsrfToken(session.csrfToken);
    return session;
  });
}

export function logout(): Promise<{ status: 'ok' }> {
  return apiRequest<{ status: 'ok' }>('/auth/logout', { method: 'POST' }).then((result) => {
    setCsrfToken(null);
    return result;
  });
}

export function enrollDevice(body: LoginRequest['device']): Promise<EnrollResponse> {
  return apiRequest<EnrollResponse>('/devices/enroll', { method: 'POST', body: body ?? { platform: 'web' } });
}

export function getEnrollmentStatus(code: string): Promise<EnrollStatusResponse> {
  return apiRequest<EnrollStatusResponse>(`/devices/enroll/${encodeURIComponent(code)}/status`);
}

/** Профили этого устройства — для экрана выбора при входе. */
export function getDeviceProfiles(): Promise<DeviceProfilesResponse> {
  return apiRequest<DeviceProfilesResponse>('/devices/me/profiles');
}

export function attachDeviceProfile(deviceId: string, userId: string): Promise<unknown> {
  return apiRequest(`/devices/${encodeURIComponent(deviceId)}/profiles`, {
    method: 'POST',
    body: { userId },
  });
}

/**
 * Передать устройство другому профилю. Владельца не отвязывают — его меняют:
 * без профиля по умолчанию устройство работать не может.
 */
export function setDeviceOwner(deviceId: string, userId: string): Promise<unknown> {
  return apiRequest(`/devices/${encodeURIComponent(deviceId)}/owner`, {
    method: 'POST',
    body: { userId },
  });
}

export function detachDeviceProfile(deviceId: string, userId: string): Promise<unknown> {
  return apiRequest(
    `/devices/${encodeURIComponent(deviceId)}/profiles/${encodeURIComponent(userId)}`,
    { method: 'DELETE' },
  );
}

/**
 * Удалить устройство из белого списка. Вместе с ним удаляется история, которую
 * оно вело на свой id (у общего телевизора без профилей она принадлежит
 * устройству).
 */
export function deleteDevice(deviceId: string): Promise<unknown> {
  return apiRequest(`/devices/${encodeURIComponent(deviceId)}`, { method: 'DELETE' });
}

export function getAdminDevices(): Promise<AdminDeviceListResponse> {
  return apiRequest<AdminDeviceListResponse>('/devices');
}

export function getSettings(): Promise<SettingsListResponse> {
  return apiRequest<SettingsListResponse>('/settings');
}

/**
 * Загрузить картинку профиля. Файл едет телом запроса как есть — без формы:
 * поле одно, и обёртка multipart тут ничего не добавляет.
 */
export function uploadUserAvatar(userId: string, file: File): Promise<AdminUser> {
  return apiRequest<AdminUser>(`/admin/users/${encodeURIComponent(userId)}/avatar`, {
    method: 'PUT',
    body: file,
    contentType: file.type || 'application/octet-stream',
  });
}

/** Убрать картинку профиля: останется эмодзи или инициал. */
export function deleteUserAvatar(userId: string): Promise<AdminUser> {
  return apiRequest<AdminUser>(`/admin/users/${encodeURIComponent(userId)}/avatar`, { method: 'DELETE' });
}

/** Индивидуальные настройки: список пользователей и их отличий от общих. */
export function getUserSettings(): Promise<UserSettingsListResponse> {
  return apiRequest<UserSettingsListResponse>('/settings/users');
}

/**
 * Пользователи Jellyfin — кого предлагать в привязке.
 *
 * Отдельным запросом от списка зрителей: за этим идут в медиатеку по сети, и
 * её недоступность не должна ронять всю страницу настроек.
 */
export function getJellyfinUsers(): Promise<JellyfinUsersResponse> {
  return apiRequest<JellyfinUsersResponse>('/settings/jellyfin-users');
}

/** Записать отличия одного зрителя. Пустой документ снимает их полностью. */
export function updateUserSettings(
  userId: string,
  override: UserSettingsOverride,
): Promise<{ userId: string; override: UserSettingsOverride }> {
  return apiRequest(`/settings/users/${encodeURIComponent(userId)}`, { method: 'PUT', body: override });
}

/**
 * Источники, известные серверу, — для настройки порядка и видимости. Ключ у
 * каждого свой и стабильный, подпись — сегодняшняя: витрины сказа
 * переименовываются, и список должен показывать то, что зритель видит сейчас.
 */
export function getSourceNames(): Promise<SourceCatalogResponse> {
  return apiRequest<SourceCatalogResponse>('/settings/source-names');
}

export function updateSettings(scope: SettingsScope, value: unknown): Promise<SettingsDocument> {
  return apiRequest<SettingsDocument>(`/settings/${encodeURIComponent(scope)}`, { method: 'PUT', body: value });
}

export function getClientConfig(): Promise<ClientConfigResponse> {
  return apiRequest<ClientConfigResponse>('/client-config');
}

export function getAdminUsers(): Promise<AdminUserListResponse> {
  return apiRequest<AdminUserListResponse>('/admin/users');
}

export function getAudit(limit = 100): Promise<AuditListResponse> {
  return apiRequest<AuditListResponse>(`/admin/audit?limit=${limit}`);
}

/** URL SSE-потока логов для EventSource (тот же origin, сессия — через cookie). */
export const LOGS_STREAM_URL = `${API_PREFIX}/admin/logs/stream`;

export function createAdminUser(body: CreateUserRequest): Promise<AdminUser> {
  return apiRequest<AdminUser>('/admin/users', { method: 'POST', body });
}

export function updateAdminUser(id: string, body: UpdateUserRequest): Promise<AdminUser> {
  return apiRequest<AdminUser>(`/admin/users/${encodeURIComponent(id)}`, { method: 'PATCH', body });
}

export function deleteAdminUser(id: string): Promise<unknown> {
  return apiRequest(`/admin/users/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function approveDevice(code: string): Promise<unknown> {
  return apiRequest(`/devices/enroll/${encodeURIComponent(code)}/approve`, { method: 'POST', body: {} });
}

export function denyDevice(code: string): Promise<{ status: 'ok' }> {
  return apiRequest<{ status: 'ok' }>(`/devices/enroll/${encodeURIComponent(code)}/deny`, { method: 'POST', body: {} });
}

/**
 * Одобрение по идентификатору устройства. Код привязки живёт минуты, а список
 * устройств — всегда, поэтому кнопка в админке опирается на id, а не на код.
 */
export function approveDeviceById(id: string, userId?: string): Promise<unknown> {
  // `userId` — кому отдать устройство; без него владельцем становится одобривший.
  return apiRequest(`/devices/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    body: userId === undefined ? {} : { userId },
  });
}

export function blockDeviceById(id: string): Promise<unknown> {
  return apiRequest(`/devices/${encodeURIComponent(id)}/block`, { method: 'POST', body: {} });
}

/**
 * Своё имя устройству. Пустая строка возвращает то, которым оно представилось
 * само: стереть имя можно, оставить устройство безымянным — нет.
 */
export function renameDevice(id: string, label: string): Promise<DeviceSummary> {
  return apiRequest<DeviceSummary>(`/devices/${encodeURIComponent(id)}/label`, {
    method: 'POST',
    body: { label },
  });
}

/**
 * Режим ожидания устройства: включить или выключить (администратор). Пока он
 * включён, страница входа сама объявляет своё устройство серверу.
 */
export function setDeviceWaiting(active: boolean): Promise<DeviceWaiting> {
  return apiRequest<DeviceWaiting>('/devices/waiting', { method: 'POST', body: { active } });
}

/**
 * Страница входа объявляет своё устройство — без логина и пароля. Сервер
 * принимает это, только пока администратор ждёт устройство; в ответ — код для
 * сверки, как при обычной привязке.
 */
export function announceDevice(trigger: 'open' | 'manual'): Promise<EnrollResponse> {
  return apiRequest<EnrollResponse>('/devices/waiting/join', {
    method: 'POST',
    body: { trigger, label: navigator.userAgent.slice(0, 80), model: navigator.platform.slice(0, 80) },
  });
}

/**
 * Лёгкая версия портала для устройства: его браузер получает `/lite/` вместо
 * обычного сайта. Решает только администратор.
 */
export function setDeviceLite(id: string, lite: boolean): Promise<DeviceSummary> {
  return apiRequest<DeviceSummary>(`/devices/${encodeURIComponent(id)}/lite`, {
    method: 'POST',
    body: { lite },
  });
}

export function revokeDevice(id: string): Promise<{ status: 'ok' }> {
  return apiRequest<{ status: 'ok' }>(`/devices/${encodeURIComponent(id)}/revoke`, { method: 'POST', body: {} });
}
/** Типизированные обёртки над реализованными маршрутами `/api/v1/*`. */

export function getHomeCatalog(signal?: AbortSignal): Promise<CatalogHomeResponse> {
  return apiRequest<CatalogHomeResponse>('/catalog/home', signal ? { signal } : {});
}

export function getMovies(page = 1, signal?: AbortSignal, genre?: string): Promise<CatalogListResponse> {
  const params = new URLSearchParams({ page: String(page) });
  // Жанр в адресе, а не отсев на клиенте: страницу отбирает витрина, иначе в
  // списке оставалась бы горстка карточек из двадцати.
  if (genre) params.set('genre', genre);
  return apiRequest<CatalogListResponse>(`/catalog/movies?${params.toString()}`, signal ? { signal } : {});
}

export function getTvShows(page = 1, signal?: AbortSignal, genre?: string): Promise<CatalogListResponse> {
  const params = new URLSearchParams({ page: String(page) });
  if (genre) params.set('genre', genre);
  return apiRequest<CatalogListResponse>(`/catalog/tv?${params.toString()}`, signal ? { signal } : {});
}

/** Фильмография человека: где он снимался, свежее первым. */
export function getPersonCredits(personId: string, signal?: AbortSignal): Promise<PersonCreditsResponse> {
  return apiRequest<PersonCreditsResponse>(
    `/catalog/person/${encodeURIComponent(personId)}`,
    signal ? { signal } : {},
  );
}

/** Другие части той же франшизы. */
export function getCollection(contentId: string, signal?: AbortSignal): Promise<CollectionResponse> {
  return apiRequest<CollectionResponse>(
    `/catalog/item/${encodeURIComponent(contentId)}/collection`,
    signal ? { signal } : {},
  );
}

/** Похожее к карточке — рекомендации витрины. */
export function getSimilar(contentId: string, signal?: AbortSignal): Promise<SimilarResponse> {
  return apiRequest<SimilarResponse>(
    `/catalog/item/${encodeURIComponent(contentId)}/similar`,
    signal ? { signal } : {},
  );
}

/**
 * Фирменный логотип тайтла (title treatment) — картинка с прозрачным фоном.
 * Лёгкий маршрут нужен главной: при смене фокуса тянуть ради логотипа всю
 * карточку с актёрами и сезонами — лишнее.
 */
export type TitleLogo = ImageRef;

export function getTitleLogo(contentId: string, signal?: AbortSignal): Promise<CatalogLogoResponse> {
  return apiRequest<CatalogLogoResponse>(
    `/catalog/item/${encodeURIComponent(contentId)}/logo`,
    signal ? { signal } : {},
  );
}

export function getFavorites(): Promise<FavoritesResponse> { return apiRequest<FavoritesResponse>('/me/favorites'); }
export function getHistory(): Promise<HistoryResponse> { return apiRequest<HistoryResponse>('/me/history'); }

/**
 * Запомненный на тайтл выбор (источник, озвучка, сезон, серия). На сервере, а не
 * в localStorage: у зрителя несколько устройств, и выбор — свойство человека, а
 * не браузера.
 */
export function getTitleChoice(contentId: string): Promise<TitleChoice> {
  return apiRequest<TitleChoice>(`/me/title-choice?contentId=${encodeURIComponent(contentId)}`);
}

export function saveTitleChoice(patch: TitleChoiceUpdate): Promise<TitleChoice> {
  // Тело передаётся ОБЪЕКТОМ: сериализует его сам `apiRequest`, и повторный
  // `JSON.stringify` здесь превратил бы запрос в строку внутри JSON — сервер
  // отвечал бы на это «некорректный запрос», и запомненный выбор молча не
  // сохранялся.
  return apiRequest<TitleChoice>('/me/title-choice', { method: 'POST', body: patch });
}

/**
 * Панель мониторинга: что происходит в установке прямо сейчас.
 *
 * Один запрос на всю картину — кто смотрит, чем это доставляется и что
 * происходило только что. Три отдельных запроса показывали бы три разных
 * момента времени.
 */
export function getAdminMonitor(signal?: AbortSignal): Promise<AdminMonitorResponse> {
  return apiRequest<AdminMonitorResponse>('/admin/monitor', signal ? { signal } : {});
}

/**
 * Карточка тайтла для всплывающей подсказки в мониторе: постер и описание.
 *
 * Идентификатор содержит двоеточия (`tmdb:movie:123`), поэтому кодируется.
 * `signal` нужен, чтобы увод курсора отменял запрос, не дожидаясь витрины.
 */
export function getMonitorTitle(contentId: string, signal?: AbortSignal): Promise<MonitorTitleCard> {
  return apiRequest<MonitorTitleCard>(
    `/admin/monitor/titles/${encodeURIComponent(contentId)}`,
    signal ? { signal } : {},
  );
}

/**
 * История поиска: двадцать последних запросов зрителя.
 *
 * На сервере и по профилю — как запомненный выбор: искал на телевизоре,
 * подсказка нужна и в браузере. Каждый вызов возвращает список целиком, поэтому
 * отдельного перечитывания после записи не нужно.
 */
export function getSearchHistory(): Promise<SearchHistoryResponse> {
  return apiRequest<SearchHistoryResponse>('/me/search-history');
}

export function rememberSearch(query: string): Promise<SearchHistoryResponse> {
  return apiRequest<SearchHistoryResponse>('/me/search-history', { method: 'POST', body: { query } });
}

export function forgetSearch(query: string): Promise<SearchHistoryResponse> {
  return apiRequest<SearchHistoryResponse>(`/me/search-history/${encodeURIComponent(query)}`, {
    method: 'DELETE',
  });
}

/**
 * Отметка «этой раздачей уже смотрели»: поставить или снять.
 *
 * Читать её отдельно не нужно — она приходит вместе с источниками (`played` у
 * каждой раздачи). Живёт на сервере, поэтому отмеченное на телевизоре видно в
 * браузере и наоборот.
 */
export function setReleaseMark(contentId: string, sourceId: string, marked: boolean): Promise<ReleaseMarkResponse> {
  return apiRequest<ReleaseMarkResponse>('/me/release-marks', {
    method: 'POST',
    body: { contentId, sourceId, marked },
  });
}

/** Стереть историю просмотров целиком. Избранное не трогает. */
export function clearHistory(): Promise<{ status: 'ok'; removed: number }> {
  return apiRequest<{ status: 'ok'; removed: number }>('/me/history', { method: 'DELETE' });
}

/**
 * Действия над одной карточкой истории — то, что клиенты показывают в меню по
 * долгому нажатию на постер. Все три работают по тайтлу целиком: за одной
 * карточкой сериала стоит столько записей, сколько серий было начато.
 */
export function removeFromHistory(contentId: string): Promise<{ status: 'ok'; removed: number }> {
  return apiRequest(`/me/history/${encodeURIComponent(contentId)}`, { method: 'DELETE' });
}

export function resetProgress(contentId: string): Promise<{ status: 'ok'; updated: number }> {
  return apiRequest(`/me/history/${encodeURIComponent(contentId)}/reset`, { method: 'POST', body: {} });
}

export function markWatched(contentId: string): Promise<{ status: 'ok'; updated: number }> {
  return apiRequest(`/me/history/${encodeURIComponent(contentId)}/watched`, { method: 'POST', body: {} });
}

/**
 * То же самое, но про ОДНУ серию: соседние не задеваются.
 *
 * Отдельные вызовы, а не необязательный параметр у прежних: там действие
 * осознанно работает по тайтлу целиком (карточка в истории — это весь сериал),
 * и подмешивать к нему серию значило бы стереть эту разницу.
 */
export function markEntryWatched(
  contentId: string,
  spot: { season: number; episode: number },
): Promise<{ status: 'ok' }> {
  return apiRequest(`/me/history/${encodeURIComponent(contentId)}/entry/watched`, {
    method: 'POST',
    body: spot,
  });
}

export function resetEntryProgress(
  contentId: string,
  spot: { season: number; episode: number },
): Promise<{ status: 'ok'; updated: boolean }> {
  return apiRequest(`/me/history/${encodeURIComponent(contentId)}/entry/reset`, {
    method: 'POST',
    body: spot,
  });
}

/**
 * Что происходит, пока поднимается раздача: раздающие, скорость, объём кэша.
 * Спрашивается, пока плеер ждёт поток, — пустой спиннер неотличим от зависания.
 */
export function getPlaybackPreparation(contentId: string): Promise<PlaybackPreparationResponse> {
  return apiRequest<PlaybackPreparationResponse>(`/playback/preparation?contentId=${encodeURIComponent(contentId)}`);
}
export function getContinueWatching(signal?: AbortSignal): Promise<ContinueWatchingResponse> { return apiRequest<ContinueWatchingResponse>('/me/continue-watching', signal ? { signal } : {}); }
/**
 * Дождаться сверки с медиатекой; `changed: true` — полку пора перечитать.
 *
 * Запрос намеренно долгий: он висит, пока сервер не закончит обход. Так полка
 * обновляется сама, без опроса по таймеру и без ухода зрителя с экрана.
 */
export function awaitContinueWatchingSync(since: number, signal?: AbortSignal): Promise<{ changed: boolean; syncRevision?: number }> { return apiRequest<{ changed: boolean; syncRevision?: number }>(`/me/continue-watching/pending?since=${since}`, signal ? { signal } : {}); }
export function addFavorite(contentId: string): Promise<{ status: 'ok' }> { return apiRequest('/me/favorites', { method: 'POST', body: { contentId } }); }
export function removeFavorite(contentId: string): Promise<{ status: 'ok' }> { return apiRequest(`/me/favorites/${encodeURIComponent(contentId)}`, { method: 'DELETE' }); }
export function saveProgress(body: ProgressUpdate): Promise<ProgressEntry> { return apiRequest<ProgressEntry>('/me/progress', { method: 'POST', body }); }

export function search(query: string, page = 1, signal?: AbortSignal): Promise<SearchResponse> {
  const params = new URLSearchParams({ q: query, page: String(page) });
  return apiRequest<SearchResponse>(`/search?${params.toString()}`, signal ? { signal } : {});
}

export function getItemDetails(
  contentId: string,
  signal?: AbortSignal,
): Promise<CatalogItemDetails> {
  return apiRequest<CatalogItemDetails>(
    `/catalog/item/${encodeURIComponent(contentId)}`,
    signal ? { signal } : {},
  );
}

/**
 * Источники выбранного класса: `torrent` — кнопка «Торренты» (включая
 * Jellyfin-файлы), `online` — «Смотреть онлайн». Без `kind` сервер опрашивает
 * всех провайдеров.
 */
export function getItemSources(
  contentId: string,
  kind?: ProviderSourceKind,
  /**
   * Область поиска у сериала. Номер серии необязателен: раздача сезона одна на
   * все его серии, и источник выбирают до серии.
   */
  scope?: { season: number; episode?: number },
  signal?: AbortSignal,
): Promise<ProviderSourcesResponse> {
  const params = new URLSearchParams();
  if (kind !== undefined) params.set('kind', kind);
  if (scope !== undefined) {
    params.set('season', String(scope.season));
    if (scope.episode !== undefined) params.set('episode', String(scope.episode));
  }
  const query = params.size === 0 ? '' : `?${params.toString()}`;
  return apiRequest<ProviderSourcesResponse>(
    `/catalog/item/${encodeURIComponent(contentId)}/sources${query}`,
    {
      // Поиск идёт по всем трекерам сразу, и самый медленный задаёт общее
      // время: пятнадцати секунд на это мало, а обрыв выглядит как «источников
      // нет», хотя они просто не успели прийти.
      timeoutMs: 90_000,
      ...(signal ? { signal } : {}),
    },
  );
}

/**
 * Озвучки выбранного онлайн-источника — для отдельного выбора. Тянутся лениво,
 * после клика по источнику; пустой список означает, что выбора озвучки нет.
 */
export function getSourceVoices(
  contentId: string,
  sourceId: string,
  /**
   * Серия, у которой спрашивать. Для сериала обязательна: в корне дерева
   * витрины лежат сезоны, а студии озвучки — уровнем ниже, у самой серии.
   * Студии по сериям одного сезона совпадают, поэтому годится и первая.
   */
  episode?: { season: number; episode: number },
  signal?: AbortSignal,
): Promise<SourceVoicesResponse> {
  const query = new URLSearchParams({ sourceId });
  if (episode !== undefined) {
    query.set('season', String(episode.season));
    query.set('episode', String(episode.episode));
  }
  return apiRequest<SourceVoicesResponse>(
    `/catalog/item/${encodeURIComponent(contentId)}/voices?${query.toString()}`,
    signal ? { signal } : {},
  );
}

/** Серии одного сезона. Грузятся только для открытого сезона. */
export function getSeasonEpisodes(
  contentId: string,
  seasonNumber: number,
  signal?: AbortSignal,
): Promise<SeasonEpisodesResponse> {
  return apiRequest<SeasonEpisodesResponse>(
    `/catalog/item/${encodeURIComponent(contentId)}/season/${seasonNumber}`,
    signal ? { signal } : {},
  );
}

/** Каналы Live TV. Адреса потоков в ответ не входят. */
export function getLiveChannels(signal?: AbortSignal): Promise<LiveChannelListResponse> {
  return apiRequest<LiveChannelListResponse>('/livetv/channels', signal ? { signal } : {});
}

/**
 * «Сейчас в эфире» и «далее» для набора каналов (по их `epgId`). Пакетом, а не
 * по каналу: страница рисует весь список сразу.
 */
export function getChannelsNowNext(
  epgIds: readonly string[],
  signal?: AbortSignal,
): Promise<EpgNowNextResponse> {
  // POST, а не GET: id-ов бывают тысячи — в длину URL они не влезают.
  return apiRequest<EpgNowNextResponse>('/livetv/epg/now-next', {
    method: 'POST',
    body: { ids: epgIds },
    ...(signal ? { signal } : {}),
  });
}

/** Полное расписание канала — для панели канала на странице ТВ. */
export function getChannelSchedule(
  epgId: string,
  signal?: AbortSignal,
): Promise<EpgScheduleResponse> {
  return apiRequest<EpgScheduleResponse>(
    `/livetv/epg/${encodeURIComponent(epgId)}`,
    signal ? { signal } : {},
  );
}

/**
 * Сессия просмотра канала. Клиент передаёт только наш id: адрес потока
 * подставляет сервер, и подменить его запросом нельзя.
 */
export function createChannelSession(channelId: string): Promise<PlaybackSessionResponse> {
  return apiRequest<PlaybackSessionResponse>(
    `/livetv/channels/${encodeURIComponent(channelId)}/session`,
    { method: 'POST', body: {} },
  );
}

export function getWebPlugins(signal?: AbortSignal): Promise<PluginListResponse> {
  return apiRequest<PluginListResponse>('/plugins?platform=web', signal ? { signal } : {});
}

export function getReadiness(signal?: AbortSignal): Promise<HealthReadyResponse> {
  return apiRequest<HealthReadyResponse>('/health/ready', signal ? { signal } : {});
}

/**
 * Возможности браузера. Отправляются серверу как подсказка: окончательное
 * решение о режиме воспроизведения принимает backend (PLAYBACK_TRANSCODING.md).
 */
export function detectCapabilities(): ClientCapabilities {
  const probe = document.createElement('video');
  const canPlay = (type: string): boolean => probe.canPlayType(type) !== '';
  // Онлайн-потоки и HLS играют через hls.js поверх Media Source Extensions, а не
  // нативным `<video>`. Для кодеков это и есть верный вопрос: `canPlayType`
  // отвечает про нативное воспроизведение и на Chrome занижает AV1/HEVC, которые
  // через MSE как раз поддерживаются. Поэтому кодек считается доступным, если его
  // принимает MSE ИЛИ нативный плеер.
  const mse = typeof MediaSource !== 'undefined' && typeof MediaSource.isTypeSupported === 'function'
    ? MediaSource
    : null;
  const canDecode = (type: string): boolean => (mse?.isTypeSupported(type) ?? false) || canPlay(type);
  // Отдельный вопрос — что берёт MSE.
  //
  // Нативный плеер и MSE поддерживают РАЗНОЕ, и разница не теоретическая:
  // HEVC часто играется нативным `<video src>` и не принимается MediaSource.
  // Сервер выбирает способ доставки (непрерывный поток или нарезка), и ему
  // нужно знать оба ответа, а не их объединение — иначе он отдаёт плейлист с
  // кодеком, который у этого клиента живёт только в нативном пути.
  const canDecodeMse = (type: string): boolean => mse?.isTypeSupported(type) ?? false;

  // Спрашиваем браузер про все контейнеры, которые вообще встречаются в
  // раздачах, а не про заранее выбранную тройку: сервер перекодирует ровно то,
  // что этот клиент не берётся открыть. Chrome, например, играет matroska с
  // H.264, и перекодировать такой файл было бы напрасной нагрузкой.
  const CONTAINER_PROBES: readonly (readonly [string, string])[] = [
    ['mp4', 'video/mp4'],
    ['webm', 'video/webm'],
    ['ogg', 'video/ogg'],
    ['mkv', 'video/x-matroska'],
    ['mov', 'video/quicktime'],
    ['ts', 'video/mp2t'],
    ['hls', 'application/vnd.apple.mpegurl'],
  ];
  const containers = CONTAINER_PROBES.filter(([, mime]) => canPlay(mime)).map(([name]) => name);
  // Пустой список означал бы «не умею ничего», и сервер перекодировал бы всё
  // подряд; mp4 играет любой браузер, в котором вообще есть <video>.
  if (containers.length === 0) containers.push('mp4');

  const videoCodecs: string[] = [];
  const mseVideoCodecs: string[] = [];
  // Профиль и глубина цвета проверяются отдельно: браузер, декодирующий
  // 8-битный HEVC (Main), сплошь и рядом не берёт 10-битный (Main10), а именно
  // Main10 лежит в тяжёлых релизах. Раньше спрашивали только про Main, клиент
  // отвечал «hevc умею», ловил 10 бит и падал уже в процессе.
  const VIDEO_PROBES: readonly (readonly [string, string])[] = [
    ['h264', 'video/mp4; codecs="avc1.640029"'],
    ['hevc', 'video/mp4; codecs="hvc1.1.6.L93.B0"'],
    ['hevc10', 'video/mp4; codecs="hvc1.2.4.L153.B0"'],
    ['vp9', 'video/webm; codecs="vp9"'],
    ['av1', 'video/mp4; codecs="av01.0.05M.08"'],
  ];
  for (const [name, mime] of VIDEO_PROBES) {
    if (canDecode(mime)) videoCodecs.push(name);
    if (canDecodeMse(mime)) mseVideoCodecs.push(name);
  }
  if (videoCodecs.length === 0) videoCodecs.push('h264');

  const audioCodecs: string[] = [];
  if (canDecode('audio/mp4; codecs="mp4a.40.2"')) audioCodecs.push('aac');
  if (canDecode('audio/mp4; codecs="ac-3"')) audioCodecs.push('ac3');
  if (canDecode('audio/mp4; codecs="ec-3"')) audioCodecs.push('eac3');
  if (canDecode('audio/webm; codecs="opus"')) audioCodecs.push('opus');
  if (canDecode('audio/mpeg')) audioCodecs.push('mp3');
  if (audioCodecs.length === 0) audioCodecs.push('aac');

  const height = typeof window.screen?.height === 'number' ? window.screen.height : 1080;

  return {
    // Часть решений сервера про звук выведена именно из поведения браузеров;
    // называем себя честно, чтобы приставка и браузер не судились одинаково.
    platform: 'web',
    containers,
    videoCodecs,
    mseVideoCodecs,
    audioCodecs,
    maxHeight: Math.min(Math.max(height, 240), 4320),
    maxFrameRate: 60,
    hdr: false,
    subtitleFormats: ['vtt'],
    drm: [],
  };
}

/**
 * Создание playback session. Передаются только внутренние идентификаторы:
 * произвольные URL/magnet API не принимает по контракту.
 *
 * Если источник подготовить не удалось, backend отвечает
 * `PLAYBACK_NOT_CONFIGURED` — UI обязан честно показать это, а не имитировать
 * воспроизведение.
 */
export function createPlaybackSession(
  contentId: string,
  options: {
    sourceId?: string;
    fileId?: string;
    episode?: { season: number; episode: number };
    voice?: string;
    signal?: AbortSignal;
  } = {},
): Promise<PlaybackSessionResponse> {
  const body: Record<string, unknown> = {
    contentId,
    quality: 'auto',
    capabilities: detectCapabilities(),
  };
  if (options.sourceId) body.sourceId = options.sourceId;
  if (options.fileId) body.fileId = options.fileId;
  if (options.voice) body.voice = options.voice;
  if (options.episode) {
    body.season = options.episode.season;
    body.episode = options.episode.episode;
  }

  return apiRequest<PlaybackSessionResponse>('/playback/session', {
    method: 'POST',
    body,
    // Подготовка источника — самая долгая операция в приложении, и штатных
    // пятнадцати секунд ей не хватает по совершенно законным причинам: раздачу
    // надо добавить в движок (magnet ждёт метаданных из DHT), дождаться разбора
    // файлов, убедиться, что она отдаёт байты, и измерить дорожки. Всё это
    // время движок работает — просто ещё не начал отдавать поток. Клиент же
    // рвал запрос и писал «сервер не ответил вовремя», хотя сервер отвечал
    // честно, только позже.
    timeoutMs: 120_000,
    ...(options.signal ? { signal: options.signal } : {}),
  });
}

/**
 * Следующая серия — продолжение той же сессии.
 *
 * Сервер берёт источник у играющей сессии, а не у списка на экране: за время
 * серии список успевает устареть, и запуск по нему упирался в «выбранный
 * источник недоступен» на источнике, который только что играл. Клиент называет
 * только себя (возможности устройства) — что и чем играть, решает сервер.
 */
export function startNextEpisode(sessionId: string): Promise<PlaybackSessionResponse> {
  return startNeighbourEpisode(sessionId, 'next-episode');
}

/** Предыдущая серия того же сезона — так же, продолжением играющей сессии. */
export function startPreviousEpisode(sessionId: string): Promise<PlaybackSessionResponse> {
  return startNeighbourEpisode(sessionId, 'previous-episode');
}

function startNeighbourEpisode(
  sessionId: string,
  route: 'next-episode' | 'previous-episode',
): Promise<PlaybackSessionResponse> {
  return apiRequest<PlaybackSessionResponse>(
    `/playback/session/${encodeURIComponent(sessionId)}/${route}`,
    {
      method: 'POST',
      body: { capabilities: detectCapabilities() },
      // Тот же долгий путь, что и у обычного запуска: раздачу надо поднять.
      timeoutMs: 120_000,
    },
  );
}

/**
 * Отчёт плеера о себе: что он делает и где стоит.
 *
 * Заодно продлевает сессию — плеер открыт, значит, она нужна. Ошибки глотаются
 * вызывающим: удар не должен ни прерывать просмотр, ни писать что-то на экран.
 */
export function reportPlaybackHeartbeat(
  sessionId: string,
  heartbeat: PlaybackHeartbeat,
): Promise<void> {
  return apiRequest<void>(`/playback/session/${encodeURIComponent(sessionId)}/heartbeat`, {
    method: 'POST',
    body: heartbeat,
  });
}

/** Состояние ранее выданной сессии: путь потока и его готовность. */
export function getPlaybackSession(
  sessionId: string,
  signal?: AbortSignal,
): Promise<PlaybackSessionStatus> {
  return apiRequest<PlaybackSessionStatus>(
    `/playback/session/${encodeURIComponent(sessionId)}`,
    signal ? { signal } : {},
  );
}

/**
 * Освобождает сессию. Вызывается при выходе из плеера: без этого upstream
 * держал бы раздачу до истечения TTL.
 */
/**
 * Смена звуковой дорожки у перекодируемой сессии.
 *
 * Нужна потому, что в перекодированном потоке дорожка одна: плееру переключать
 * нечего, выбор делает ffmpeg на сервере. При прямой отдаче этот вызов не
 * используется — там все дорожки внутри потока.
 */
export function selectPlaybackAudioTrack(sessionId: string, track: number): Promise<void> {
  return apiRequest<void>(`/playback/session/${encodeURIComponent(sessionId)}/audio`, {
    method: 'POST',
    body: { track },
  });
}

/**
 * Принудительное качество потока: разрешение и/или битрейт. `null` в поле —
 * снять принуждение.
 *
 * Включает перекодирование: варианта с нужным качеством у источника обычно нет.
 * После вызова путь потока мог смениться (файл против плейлиста), поэтому
 * состояние сессии нужно перечитать, а не играть прежний адрес.
 */
export function selectPlaybackQuality(
  sessionId: string,
  quality: { bitrateKbps: number | null; maxHeight: number | null },
): Promise<void> {
  return apiRequest<void>(`/playback/session/${encodeURIComponent(sessionId)}/quality`, {
    method: 'POST',
    body: quality,
    // Первое включение перекодирования дороже последующих: сервер измеряет
    // источник (длительность, дорожки) и разбирает его нарезку. Штатных 15 c на
    // это не хватает, и запрос обрывался таймаутом — при том, что сервер работу
    // доводил до конца, и следующая попытка отвечала мгновенно.
    timeoutMs: 60_000,
  });
}

export function reportPlaybackError(
  sessionId: string,
  report: PlaybackErrorReport,
): Promise<PlaybackSessionStatus> {
  return apiRequest<PlaybackSessionStatus>(`/playback/session/${encodeURIComponent(sessionId)}/report-error`, {
    method: 'POST',
    body: report,
    timeoutMs: 60_000,
  });
}

export function endPlaybackSession(sessionId: string): Promise<void> {
  return apiRequest<void>(`/playback/session/${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
  });
}
